import { useEffect, useState } from 'react';
import type { StepProps } from '../engine/context.ts';
import { TitleBlock } from '../ui/TitleBlock.tsx';
import { Icon } from '../ui/Icon.tsx';
import { asset } from '../engine/assets.ts';
import { haptic } from '../engine/haptics.ts';
import { CREAM, GRAD, INK, LINE, MUTED, ORANGE, PAPER, SUN } from '../ui/theme.ts';

/** Three 104×185 tiles + a "no cold open" row. Answer: { frame, noHook, t }. */
export function TapFrame({ step, content, ctx }: StepProps) {
  const key = step.answerKey!;
  const frames = ctx.shared.frames ?? [];
  const [frame, setFrame] = useState<number | null>(null);
  const [noHook, setNoHook] = useState(false);

  useEffect(() => {
    ctx.setCta({ label: content.cta ?? ctx.shared.ui?.next ?? 'Next', disabled: frame === null && !noHook, onPress: ctx.next });
  }, [step.id, frame, noHook, content.cta]);

  const pickFrame = (i: number) => {
    haptic();
    setFrame(i);
    setNoHook(false);
    ctx.setAnswer(key, { frame: i, noHook: false, t: frames[i]?.t ?? null });
  };
  const pickNone = () => {
    haptic();
    setFrame(null);
    setNoHook(true);
    ctx.setAnswer(key, { frame: null, noHook: true, t: null });
  };

  return (
    <>
      <TitleBlock label={content.label} title={content.title} highlight={content.highlight} subtitle={content.subtitle} />

      {ctx.shared.badgeTapFrame ? (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <span style={{ padding: '6px 12px', borderRadius: 999, background: SUN, fontSize: 12, fontWeight: 800 }}>{ctx.shared.badgeTapFrame}</span>
        </div>
      ) : null}

      <div role="radiogroup" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
        {frames.map((f, i) => {
          const on = frame === i && !noHook;
          return (
            <button
              key={f.asset}
              type="button"
              role="radio"
              aria-checked={on}
              aria-label={`Frame at ${f.t}`}
              onClick={() => pickFrame(i)}
              style={{
                position: 'relative',
                padding: 0,
                aspectRatio: '104/185',
                borderRadius: 16,
                overflow: 'hidden',
                cursor: 'pointer',
                background: INK,
                border: `3px solid ${on ? ORANGE : 'transparent'}`,
              }}
            >
              <span style={{ position: 'absolute', inset: 0, backgroundSize: 'cover', backgroundPosition: 'center', backgroundImage: `url(${asset(f.asset)})` }} />
              <span style={{ position: 'absolute', left: 8, bottom: 8, padding: '3px 8px', borderRadius: 999, background: 'rgba(255,255,255,.92)', fontSize: 11, fontWeight: 800 }}>{f.t}</span>
              <span
                style={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  border: '2px solid #fff',
                  background: on ? GRAD : 'rgba(31,27,45,.3)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                }}
              >
                <i className="ph-bold ph-check" style={{ fontSize: 13, opacity: on ? 1 : 0 }} aria-hidden="true" />
              </span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        role="radio"
        aria-checked={noHook}
        onClick={pickNone}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minHeight: 56,
          padding: '10px 14px',
          borderRadius: 20,
          textAlign: 'left',
          cursor: 'pointer',
          font: 'inherit',
          color: INK,
          background: noHook ? CREAM : PAPER,
          border: `2px solid ${noHook ? ORANGE : LINE}`,
        }}
      >
        <Icon name="film-slate" size={24} color={MUTED} />
        <span style={{ fontWeight: 600, flex: 1 }}>{ctx.shared.noHookLabel ?? 'No cold open'}</span>
      </button>
    </>
  );
}
